import React, { useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Typography, Card, Button } from '@totalsoft/rocket-ui';
import { Box, Grid, TableBody, TableHead, Menu, MenuItem } from '@mui/material';
import { StyledGrayIcon } from 'components/common/Styles';
import { StyledTable, StyledTableRow, StyledHeaderCell } from 'components/common/Styles';
import { Info } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { sequence, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import PropertyValueItem from './PropertyValueItem';
import ImportValuesModal from './ImportValuesModal';
import { pvtUnspecified, pvtRange, pvtList } from './constants';
import { get } from '@totalsoft/change-tracking-react';
import { usePermission } from 'providers/RightsHooks';
import useConfirmDialog from 'features/common/components/confirm/useConfirmDialog';
import { ADMIN_ITEMS_WRITE } from 'constants/rightsCodes';

const PropertyValuesComponent = ({
  lens,
  propertyTypeCode,
  propertyDetails,
  propertyValueTypeCode,
  defaultValue,
  onSelectionChanged,
  onSaveValue,
  onDeletePropertyValue,
  onAddNewValue,
  onCancelEditValue,
  onImportValues,
  onLoadPropertyDetails,
  isNewProperty
}) => {
  const [propertyValueLens, dirtyInfo, resetLens] = useChangeTrackingLens();
  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [splitButton, setSplitButton] = useState(null);
  const { t } = useTranslation();
  const confirm = useConfirmDialog();
  const canWrite = usePermission(ADMIN_ITEMS_WRITE);

  useEffect(() => {
    resetLens(lens |> get);
  }, [resetLens, lens]);

  const isPropertyDetailSelected = useCallback(
    valueDetail => propertyValueLens && (propertyValueLens |> sequence).some(x => (x.value |> get) === valueDetail),
    [propertyValueLens]
  );

  const onDelete = propertyValueId => {
    confirm(
      () => {
        onDeletePropertyValue(propertyValueId);
      },
      {
        content: t('Items.Values.DeleteValue')
      }
    );
  };

  const handleClick = useCallback(event => {
    setSplitButton(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setSplitButton(null);
  }, []);

  const handleImportFromDatabase = () => {
    setShowSearchModal(true);
  };

  const unsavedChanges = useMemo(() => {
    const newPropertyValue = (propertyValueLens |> sequence)?.find(x => (x.propertyValueId |> get) === null);
    return Boolean(newPropertyValue);
  }, [propertyValueLens]);

  return (
    <Card title={t('Items.Values.Title')}>
      <Grid container spacing={1} id='parameterList'>
        <Grid container alignItems={'center'} justifyContent={'center'}>
          {propertyValueTypeCode === pvtUnspecified && (
            <>
              <Info fontSize='small' color='inherit' />
              <span>{t('Items.Values.AddValuesOnUnspecifiedPvt')}</span>
            </>
          )}
        </Grid>
        {propertyValueTypeCode !== pvtUnspecified && (
          <StyledTable>
            <TableHead>
              <StyledTableRow>
                {propertyValueTypeCode === pvtList && (
                  <StyledHeaderCell style={{ textAlign: 'start', width: '15%' }}>{t('Items.Properties.DefaultValue')}</StyledHeaderCell>
                )}
                <StyledHeaderCell style={{ textAlign: 'start', width: propertyValueTypeCode === pvtList ? '70%' : '30%' }}>
                  {t('Items.Properties.Value')}
                </StyledHeaderCell>
                {propertyValueTypeCode === pvtRange && (
                  <>
                    <StyledHeaderCell style={{ textAlign: 'start', width: '30%' }}>{t('Items.Values.MinValue')}</StyledHeaderCell>
                    <StyledHeaderCell style={{ textAlign: 'start', width: '30%' }}>{t('Items.Values.MaxValue')}</StyledHeaderCell>
                  </>
                )}
                <StyledHeaderCell style={{ textAlign: 'right' }}>
                  <Button
                    id='Items_AddValue'
                    aria-controls='split-button-menu'
                    aria-haspopup='true'
                    onClick={handleClick}
                    variant='contained'
                    color='primary'
                    disabled={propertyValueTypeCode === pvtUnspecified || isNewProperty || !canWrite}
                    ref={splitButton}
                  >
                    {`+ ${t('Add')}`}
                  </Button>
                  <Menu
                    id='split-button-menu'
                    anchorEl={splitButton}
                    keepMounted
                    open={Boolean(splitButton)}
                    onClose={handleClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  >
                    <MenuItem onClick={onAddNewValue} disabled={unsavedChanges || !canWrite}>
                      {t('Items.Values.AddNewValue')}
                    </MenuItem>
                    <MenuItem disabled={propertyValueTypeCode !== pvtList || !canWrite} onClick={handleImportFromDatabase}>
                      {t('Items.Values.ImportValuesFromDataBase')}
                    </MenuItem>
                  </Menu>
                </StyledHeaderCell>
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {propertyValueLens &&
                (propertyValueLens |> sequence).map((value, index) => {
                  return (
                    <PropertyValueItem
                      key={index}
                      index={index}
                      value={value}
                      propertyValueTypeCode={propertyValueTypeCode}
                      propertyTypeCode={propertyTypeCode}
                      onSaveValue={onSaveValue}
                      onCancelEditValue={onCancelEditValue}
                      onDelete={onDelete}
                      defaultValue={defaultValue}
                      onSelectionChanged={onSelectionChanged}
                      isDirty={isDirty}
                    />
                  );
                })}
            </TableBody>
          </StyledTable>
        )}
        <Grid container alignItems={'center'} justifyContent={'center'}>
          {isNewProperty && (
            <Box display='flex' gap={1} alignItems='center' justifyContent='center'>
              <StyledGrayIcon fontSize='small'>error</StyledGrayIcon>
              <Typography color='textSecondary'>{t('Items.Values.SaveValidation')}</Typography>
            </Box>
          )}
          {propertyValueLens &&
            (propertyValueLens |> sequence)?.length <= 0 &&
            !isNewProperty &&
            propertyValueTypeCode !== pvtUnspecified && (
              <Box display='flex' gap={1} alignItems='center' justifyContent='center'>
                <StyledGrayIcon fontSize='small'>error</StyledGrayIcon>
                <Typography color='textSecondary'>{t('ResultsNotFound')}</Typography>
              </Box>
            )}
        </Grid>
        {showSearchModal && (
          <ImportValuesModal
            onCloseModal={() => setShowSearchModal(false)}
            onLoadPropertyDetails={onLoadPropertyDetails}
            propertyDetails={propertyDetails}
            isPropertyDetailSelected={isPropertyDetailSelected}
            onImportValues={onImportValues}
          />
        )}
      </Grid>
    </Card>
  );
};

PropertyValuesComponent.propTypes = {
  lens: PropTypes.object,
  propertyTypeCode: PropTypes.string,
  propertyDetails: PropTypes.array,
  propertyValueTypeCode: PropTypes.string,
  defaultValue: PropTypes.string,
  onSelectionChanged: PropTypes.func,
  onSaveValue: PropTypes.func,
  onDeletePropertyValue: PropTypes.func,
  onAddNewValue: PropTypes.func,
  onCancelEditValue: PropTypes.func,
  onImportValues: PropTypes.func,
  onLoadPropertyDetails: PropTypes.func,
  isNewProperty: PropTypes.bool
};

export default PropertyValuesComponent;
